import React, { useState } from 'react';
import RenderBlocks from '@plone/volto/components/theme/View/RenderBlocks';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import { deadline, fileFormat, fileSize, meetWhen, shortDay } from './format';
import { titleFit } from '../Listing/meetDates';
import {
  AddToCalendarLink,
  CopyFeedButton,
  SubscribeLink,
} from '../Calendar/Calendar';
import {
  EditingMessage,
  FilingControl,
  UploadButton,
  useMeetEditing,
} from './MeetEditing';

const PILL = {
  posted: { className: 'ok', label: 'Posted' },
  not_posted: { className: 'wait', label: 'Not posted yet' },
  after_meet: { className: 'wait', label: 'After the meet' },
};

// The old way of listing documents in the meet's text, which this page now
// shows as rows: hide those lines from "About this meet"
const DOCUMENT_LINE =
  /^(meet\s+)?(summons|entry file|entries|psych sheet|results?|session report|medal\w*|scores?|record breakers?|high point winners?)\b/i;

const Row = ({ title, status, item, extraTitle, editing, slotId }) => {
  const pill = PILL[status];
  const inner = (
    <>
      <span className="asaj-doc-icon" aria-hidden="true">
        {item ? fileFormat(item) : '—'}
      </span>
      <span className="asaj-doc-text">
        <span className="asaj-doc-title">{title}</span>
        <span className="asaj-doc-meta">
          <span className={`asaj-pill ${pill.className}`}>{pill.label}</span>
          {item && (
            <span>
              {extraTitle || editing ? `${item.title} · ` : ''}
              {fileFormat(item)}, {fileSize(item.size)}
            </span>
          )}
        </span>
      </span>
    </>
  );
  if (!item) {
    return (
      <div className="asaj-doc off">
        {inner}
        {editing && (
          <UploadButton editing={editing} slotId={slotId}>
            Upload
          </UploadButton>
        )}
      </div>
    );
  }
  const link = (
    <UniversalLink
      className="asaj-doc"
      href={`${flattenToAppURL(item['@id'])}/@@display-file/file`}
    >
      {inner}
      <span className="asaj-doc-go">Open</span>
    </UniversalLink>
  );
  if (!editing) return link;
  return (
    <div className="asaj-doc-editable">
      {link}
      <FilingControl editing={editing} item={item} />
    </div>
  );
};

// One slot: a row per document, or a single "not yet" row
const Slot = ({ slot, editing }) =>
  slot.items.length ? (
    slot.items.map((item) => (
      <Row
        key={item['@id']}
        title={slot.title}
        status="posted"
        item={item}
        extraTitle={slot.items.length > 1}
        editing={editing}
      />
    ))
  ) : (
    <Row
      title={slot.title}
      status={slot.status}
      editing={editing}
      slotId={slot.id}
    />
  );

const LiveRow = ({ live, start }) => {
  if (!live.available) {
    return (
      <div className="asaj-doc off">
        <span className="asaj-doc-icon" aria-hidden="true">
          —
        </span>
        <span className="asaj-doc-text">
          <span className="asaj-doc-title">Live results</span>
          <span className="asaj-doc-meta">
            ASAJ isn't running live results for this meet.
          </span>
        </span>
      </div>
    );
  }
  const pill = {
    live: { className: 'live', label: 'Live now' },
    upcoming: { className: 'wait', label: `Opens ${shortDay(start)}` },
    closed: { className: 'ok', label: 'Meet finished' },
  }[live.status];
  return (
    <a
      className={`asaj-doc ${live.status === 'live' ? 'is-live' : ''}`}
      href={live.url}
      target="_blank"
      rel="noopener noreferrer"
    >
      <span className="asaj-doc-icon" aria-hidden="true">
        LIVE
      </span>
      <span className="asaj-doc-text">
        <span className="asaj-doc-title">Live results</span>
        <span className="asaj-doc-meta">
          <span className={`asaj-pill ${pill.className}`}>{pill.label}</span>
          <span>liveresults.asaj.com.jm</span>
        </span>
      </span>
      <span className="asaj-doc-go">
        {live.status === 'live' ? 'Follow live' : 'Open'}
      </span>
    </a>
  );
};

const TABS = [
  { id: 'documents', title: 'Documents' },
  { id: 'psych', title: 'Psych sheet', slot: 'psych_sheet' },
  { id: 'live', title: 'Live' },
  { id: 'results', title: 'Results', slot: 'results' },
];

const tabDot = (tab, slots, live) => {
  if (tab.id === 'live') {
    if (!live.available) return null;
    return live.status === 'live'
      ? 'live'
      : live.status === 'closed'
        ? 'ok'
        : 'wait';
  }
  if (!tab.slot) return null;
  return slots[tab.slot].status === 'posted' ? 'ok' : 'wait';
};

// The view for every meet (Event): documents and their status, live
// results and the meet's details. The data comes from the meet-documents
// expander (asaj.site), which works out every status on the server.
const MeetView = ({ content }) => {
  const data = content['@components']?.['meet-documents'];
  const live = data?.live_results;
  const [tab, setTab] = useState(data?.meet?.live ? 'live' : 'documents');
  const editing = useMeetEditing(
    flattenToAppURL(content['@id']),
    data?.slots ?? [],
  );

  if (!data?.slots) {
    // Not loaded with the expander (e.g. a stale client); show the text
    return <RenderBlocks content={content} />;
  }

  const slots = Object.fromEntries(data.slots.map((s) => [s.id, s]));
  const { meet } = data;
  const meetPath = flattenToAppURL(content['@id']);

  const aboutItems = (content.blocks_layout?.items || []).filter((id) => {
    const block = content.blocks?.[id];
    if (!block) return false;
    if (['title', 'description'].includes(block['@type'])) return false;
    if (block['@type'] === 'slate') {
      const text = (block.plaintext || '').trim();
      return text && !DOCUMENT_LINE.test(text);
    }
    return true;
  });

  const onKeyDown = (event) => {
    const index = TABS.findIndex((t) => t.id === tab);
    const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
    if (!step) return;
    event.preventDefault();
    const next = TABS[(index + step + TABS.length) % TABS.length];
    setTab(next.id);
    document.getElementById(`asaj-tab-${next.id}`)?.focus();
  };

  return (
    // #page-document: VLT and Volto style content (and the blocks in
    // "About this meet") under it, as in the default view
    <div id="page-document" className="asaj-meet">
      <section className="asaj-phead">
        <div className="asaj-phead-inner">
          <h1 className="asaj-display" style={titleFit(content.title)}>
            {content.title}
          </h1>
          <p className="asaj-when">
            {meetWhen(meet.start, meet.end, content.whole_day)}
          </p>
          <p className="asaj-cal">
            <AddToCalendarLink path={meetPath}>
              Add to calendar
            </AddToCalendarLink>
            <SubscribeLink label="Subscribe to all meets">
              Subscribe<span className="asaj-wide">{'\u00a0'}to all meets</span>
            </SubscribeLink>
            <CopyFeedButton className="asaj-copy" />
          </p>
          {content.description && (
            <p className="asaj-lead">{content.description}</p>
          )}
          <div className="asaj-tabs" role="tablist" aria-label="Meet sections">
            {TABS.map((t) => {
              const dot = tabDot(t, slots, live);
              const selected = t.id === tab;
              return (
                <button
                  key={t.id}
                  id={`asaj-tab-${t.id}`}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  aria-controls="asaj-meet-panel"
                  tabIndex={selected ? 0 : -1}
                  onClick={() => setTab(t.id)}
                  onKeyDown={onKeyDown}
                >
                  {dot && (
                    <span className={`asaj-tdot ${dot}`} aria-hidden="true" />
                  )}
                  {t.title}
                </button>
              );
            })}
          </div>
        </div>
        <span className="asaj-flag" aria-hidden="true" />
      </section>

      <div className="asaj-meet-body">
        <div
          id="asaj-meet-panel"
          role="tabpanel"
          aria-labelledby={`asaj-tab-${tab}`}
          className="asaj-meet-panel"
        >
          {editing && <EditingMessage editing={editing} />}
          {tab === 'documents' && (
            <>
              <Slot slot={slots.summons} editing={editing} />
              <Slot slot={slots.entry_file} editing={editing} />
              <Slot slot={slots.psych_sheet} editing={editing} />
              <LiveRow live={live} start={meet.start} />
              <Slot slot={slots.results} editing={editing} />
            </>
          )}
          {tab === 'psych' && (
            <Slot slot={slots.psych_sheet} editing={editing} />
          )}
          {tab === 'live' && <LiveRow live={live} start={meet.start} />}
          {tab === 'results' && <Slot slot={slots.results} editing={editing} />}
          {['documents', 'results'].includes(tab) && data.other.length > 0 && (
            <>
              <h2 className="asaj-meet-subhead">More documents</h2>
              {data.other.map((item) => (
                <Row
                  key={item['@id']}
                  title={item.title}
                  status="posted"
                  item={item}
                  editing={editing}
                />
              ))}
            </>
          )}
          {editing && tab === 'documents' && (
            <div className="asaj-doc-add">
              <UploadButton editing={editing}>Add a document</UploadButton>
              <span>
                We'll file it from its name and tell you where it went.
              </span>
            </div>
          )}
        </div>

        <aside className="asaj-meet-aside">
          {live.available && (
            <div className={`asaj-callout ${meet.live ? 'is-live' : ''}`}>
              <h2>{meet.live ? 'Live now' : 'Live results'}</h2>
              <p>
                {meet.live
                  ? 'Follow each heat as it finishes, by swimmer or by club.'
                  : 'Results from ASAJ meets are posted live at liveresults.asaj.com.jm.'}
              </p>
              <a
                className="asaj-btn asaj-btn-gold"
                href={live.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {meet.live ? 'Follow live results' : 'Open live results'}
              </a>
            </div>
          )}
          {/* The dates and calendar links are in the header */}
          {(meet.venue || meet.entry_deadline) && (
            <div className="asaj-info">
              <dl>
                {meet.venue && (
                  <>
                    <dt>Venue</dt>
                    <dd>{meet.venue}</dd>
                  </>
                )}
                {meet.entry_deadline && (
                  <>
                    <dt>Entry deadline</dt>
                    <dd>{deadline(meet.entry_deadline)}</dd>
                  </>
                )}
              </dl>
            </div>
          )}
        </aside>
      </div>

      {aboutItems.length > 0 && (
        <section className="asaj-meet-about">
          <h2 className="asaj-meet-subhead">About this meet</h2>
          <RenderBlocks
            content={{ ...content, blocks_layout: { items: aboutItems } }}
          />
        </section>
      )}
    </div>
  );
};

export default MeetView;
